import React, { useState } from 'react';
import { Palette, MousePointerClick, Zap } from 'lucide-react';
import { Card } from './ui/card';
import useInView from '../hooks/useInView';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../translations/translations';

const ServicesSection = () => {
  const [titleRef, titleInView] = useInView();
  const [gridRef, gridInView] = useInView();
  const { language } = useLanguage();
  const [activeCard, setActiveCard] = useState(null);

  const services = [
    {
      icon: <Palette className="w-12 h-12 icon-primary" />,
      titleKey: "services.service1.title",
      descriptionKey: "services.service1.description",
      subtitleKey: "services.service1.features.0",
      fillColor: "from-[#DC1A5F]/20 to-[#DC1A5F]/40"
    },
    {
      icon: <MousePointerClick className="w-12 h-12 icon-secondary" />,
      titleKey: "services.service2.title",
      descriptionKey: "services.service2.description",
      subtitleKey: "services.service2.features.0",
      fillColor: "from-[#32D6E0]/20 to-[#32D6E0]/40"
    },
    {
      icon: <Zap className="w-12 h-12 icon-primary" />,
      titleKey: "services.service3.title",
      descriptionKey: "services.service3.description",
      subtitleKey: "services.service3.features.0",
      fillColor: "from-[#DC1A5F]/20 to-[#32D6E0]/40"
    }
  ];

  return (
    <section id="services" className="pt-0 pb-8 bg-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Header */}
        <div 
          ref={titleRef}
          className={`text-center mb-16 transition-all duration-1200 ease-out ${
            titleInView 
              ? 'opacity-100 translate-y-0 scale-100' 
              : 'opacity-0 translate-y-16 scale-95'
          }`}
          style={{paddingTop: '140px'}}
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
            <span className="gradient-text">{getTranslation(language, 'services.title')}</span>
          </h2>
        </div>

        {/* Services Grid */}
        <div 
          ref={gridRef}
          className="grid md:grid-cols-3 gap-8 mb-20"
        >
          {services.map((service, index) => {
            // Animation différente pour chaque carte
            const getAnimationClass = () => {
              if (!gridInView) return '';
              if (index === 0) return 'animate-slide-in-left';
              if (index === 1) return 'animate-slide-in-bottom';
              return 'animate-slide-in-right';
            };

            return (
              <Card 
                key={index}
                className={`service-card relative overflow-hidden bg-gray-900/50 border-gray-800 p-8 text-center cursor-pointer hover:scale-110 hover:shadow-[0_20px_60px_rgba(220,26,95,0.4)] hover:border-[#DC1A5F] hover:-translate-y-2 group transition-all duration-300 ease-out ${getAnimationClass()} ${activeCard === index ? 'scale-110 shadow-[0_20px_60px_rgba(220,26,95,0.4)] border-[#DC1A5F] -translate-y-2' : ''}`}
                style={{
                  animationDelay: `${500 + index * 250}ms`,
                  opacity: gridInView ? 1 : 0
                }}
                onTouchStart={() => setActiveCard(index)}
                onTouchEnd={() => setActiveCard(null)}
                onTouchCancel={() => setActiveCard(null)}
              >
              {/* Background fill effect */}
              <div className={`absolute inset-0 bg-gradient-to-br ${service.fillColor} ${activeCard === index ? 'opacity-100' : 'opacity-0'} group-hover:opacity-100 transition-opacity duration-500`}></div>
              
              {/* Shimmer effect */}
              <div className={`absolute inset-0 ${activeCard === index ? 'opacity-100' : 'opacity-0'} group-hover:opacity-100 transition-opacity duration-500`}>
                <div className={`absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent transform -skew-x-12 ${activeCard === index ? 'translate-x-full' : '-translate-x-full'} group-hover:translate-x-full transition-transform duration-1000`}></div>
              </div>

              {/* Glow effect */}
              <div className={`absolute inset-0 rounded-lg ${activeCard === index ? 'opacity-100' : 'opacity-0'} group-hover:opacity-100 transition-opacity duration-500 shadow-2xl group-hover:shadow-[0_0_30px_rgba(220,26,95,0.3)]`}></div>

              <div className="relative z-10">
                <div 
                  className={`inline-flex p-4 rounded-2xl ${activeCard === index ? 'bg-gray-700 shadow-lg scale-110' : 'bg-gray-800'} mb-6 group-hover:bg-gray-700 transition-all duration-300 group-hover:shadow-lg group-hover:scale-110 ${gridInView ? 'animate-icon-pop' : ''}`}
                  style={{ animationDelay: `${500 + index * 250 + 400}ms` }}
                >
                  {service.icon}
                </div>
                <h3 className={`text-2xl font-bold mb-6 ${activeCard === index ? 'text-white' : 'text-white'} group-hover:text-white transition-colors duration-300`}>
                  {getTranslation(language, service.titleKey)}
                </h3>
                <p className={`text-base leading-relaxed mb-4 ${activeCard === index ? 'text-white' : 'text-gray-200'} group-hover:text-white transition-colors duration-300`}>
                  {getTranslation(language, service.descriptionKey)}
                </p>
                <p className={`text-sm leading-relaxed ${activeCard === index ? 'text-gray-200' : 'text-gray-400'} group-hover:text-gray-200 transition-colors duration-300`}>
                  {getTranslation(language, service.subtitleKey)}
                </p>
              </div>
            </Card>
            );
          })}
        </div>

      </div>
    </section>
  );
};

export default ServicesSection;